import React from "react";
import { Icon } from "@iconify/react";
import { HStack, VStack, ButtonPrimary } from "../../../components/utils";
import { formatDisplayDate } from "../../../utils/date";
import { IPlanDocument } from "../../../api/planDocumentsApi";
import { DocumentType } from "../../../types/DocumentsInterface";
import { NewCollapsibleComponent as CollapsibleComponent } from "../../../components/shared/NewCollapsibleComponent";
import LazyPdfDocument from "../../../components/shared/LazyPdfDocument";
import NewTooltip from "../../../components/shared/NewToolTip";
import Tooltip from "../../../components/shared/Tooltip";

interface PlanDocumentListProps {
  documents: IPlanDocument[];
  selectedDoc: IPlanDocument | null;
  setSelectedDoc: (doc: IPlanDocument | null) => void;
  accordionOpened: { [key: string]: boolean };
  handleAccordionClick: (section: string) => void;
  handleDelete: (docId: string | undefined) => void;
  handleEdit: (doc: IPlanDocument) => void;
  DOCUMENT_TYPES: { label: string; value: DocumentType }[];
}

export const PlanDocumentList: React.FC<PlanDocumentListProps> = ({
  documents,
  selectedDoc,
  setSelectedDoc,
  accordionOpened,
  handleAccordionClick,
  handleDelete,
  handleEdit,
  DOCUMENT_TYPES,
}) => {
  const groupedDocuments = documents.reduce((acc, doc) => {
    const type = doc.documentType || "Other";
    if (!acc[type]) acc[type] = [];
    acc[type].push(doc);
    return acc;
  }, {} as Record<string, IPlanDocument[]>);

  const downloadFile = (doc: IPlanDocument) => {
    if (doc.s3Url) {
      window.open(doc.s3Url, "_blank");
    }
  };

  const renderPreview = () => {
    if (!selectedDoc) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-4">
          <Icon
            icon="mdi:file-document-outline"
            width={64}
            className="text-gray-300"
          />
          <p>Select a document to preview</p>
        </div>
      );
    }

    if (
      selectedDoc.type === "application/pdf" ||
      selectedDoc.s3Url?.toLowerCase().endsWith(".pdf")
    ) {
      return <LazyPdfDocument url={selectedDoc.s3Url || ""} />;
    }

    return (
      <div className="flex flex-col items-center justify-center h-full gap-4 overflow-hidden">
        <Icon icon="mdi:file-download" width={64} className="text-gray-400" />
        <p className="text-gray-600">
          Preview not available for this file type
        </p>
        <ButtonPrimary onClick={() => downloadFile(selectedDoc)}>
          Download Document
        </ButtonPrimary>
      </div>
    );
  };

  return (
    <HStack className="h-full">
      {/* Sidebar List */}
      <div className="w-[40%] border-r border-gray-200 flex flex-col h-full">
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 custom-scrollbar">
          {DOCUMENT_TYPES.map((typeObj) => {
            const type = typeObj.value;
            const label = typeObj.label;
            const docs = groupedDocuments[type] || [];
            return (
              <CollapsibleComponent
                key={type}
                label={label}
                labelDetail=""
                isOpen={accordionOpened[type] ?? true}
                onClick={() => handleAccordionClick(type)}
                numberOfItems={docs.length}
              >
                {docs.length === 0 ? (
                  <div className="p-4 text-center text-sm text-gray-400 italic">
                    No documents
                  </div>
                ) : (
                  <VStack className="gap-2 mt-2">
                    {docs.map((doc) => (
                      <div
                        key={doc.id}
                        className={`p-4 cursor-pointer transition-all duration-200 rounded-lg border group ${
                          selectedDoc?.id === doc.id
                            ? "bg-orange-50 border-orange-200"
                            : "bg-white border-gray-200 hover:bg-gray-50"
                        }`}
                        onClick={() => setSelectedDoc(doc)}
                      >
                        <div className="flex items-start gap-3">
                          <VStack className="flex-1 min-w-0">
                            <HStack className="justify-between items-center align-middle">
                              <HStack className="align-middle text-center items-center gap-1 min-w-0">
                                <HStack className="font-medium text-gray-900 text-sm truncate">
                                  {(() => {
                                    const name = doc.documentName || "Untitled";
                                    return name.length > 30
                                      ? `${name.slice(0, 30)}...`
                                      : name;
                                  })()}
                                </HStack>
                              </HStack>
                              <HStack className="flex items-center gap-2 pl-2 mt-1 text-xs text-gray-500 flex-shrink-0">
                                <span className="flex items-center gap-1 whitespace-nowrap">
                                  {doc.generatedOn
                                    ? formatDisplayDate(
                                        new Date(doc.generatedOn)
                                      )
                                    : "-"}
                                </span>
                                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEdit(doc);
                                    }}
                                    className="p-1 text-gray-400 hover:text-orange-500"
                                    title="Edit Document"
                                  >
                                    <Icon icon="mdi:edit" width={14} />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDelete(doc.id);
                                    }}
                                    className="p-1 text-gray-400 hover:text-red-500"
                                    title="Delete Document"
                                  >
                                    <Icon icon="mdi:delete" width={14} />
                                  </button>
                                </div>
                              </HStack>
                            </HStack>
                            <HStack className="flex items-center gap-2 text-center">
                              <Icon
                                icon="vscode-icons:file-type-pdf2"
                                width={20}
                                className="text-gray-500 mt-1"
                              />
                              <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded text-xs">
                                {doc.isUserUploaded
                                  ? "User-Uploaded"
                                  : "System-Generated"}
                              </span>
                            </HStack>
                          </VStack>
                        </div>
                      </div>
                    ))}
                  </VStack>
                )}
              </CollapsibleComponent>
            );
          })}
        </div>
      </div>

      <div className="w-[60%] flex flex-col h-full">
        <div className="px-6 py-5 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              {selectedDoc ? selectedDoc.documentName || "Untitled" : "Preview"}
            </h2>
            <div className="flex items-center gap-3">
              {selectedDoc && (
                <button
                  onClick={() => downloadFile(selectedDoc)}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200"
                >
                  <Icon icon="mdi:download" width={18} />
                  Download
                </button>
              )}
            </div>
          </div>
          {selectedDoc && (
            <VStack className="items-start gap-2">
              <div className="flex items-center gap-4 mt-3 text-xs text-gray-600">
                <span>
                  Generated On:{" "}
                  {selectedDoc.generatedOn
                    ? formatDisplayDate(new Date(selectedDoc.generatedOn))
                    : "-"}
                </span>
                {selectedDoc.metaData &&
                  selectedDoc.metaData.shownToEmployee && (
                    <span className="text-[10px] px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                      Shown to Employee
                    </span>
                  )}
              </div>
              <div>
                {selectedDoc.metaData?.tags &&
                  selectedDoc.metaData.tags.length > 0 && (
                    <HStack className="items-center gap-2 flex-wrap">
                      <span className="text-xs text-gray-500">Tags:</span>
                      {selectedDoc.metaData.tags.map((tag, idx) => (
                        <span
                          key={idx}
                          className="text-[10px] px-2 py-0.5 bg-gray-100 text-gray-600 rounded"
                        >
                          {tag.label}
                        </span>
                      ))}
                    </HStack>
                  )}
              </div>
            </VStack>
          )}
        </div>
        <div className="flex-1 overflow-y-auto p-8 bg-gray-50">
          {renderPreview()}
        </div>
      </div>
    </HStack>
  );
};
